import { useCallback, useMemo, useState } from "react";

import { AppButton, HealthChange, LtvChange } from "app/components/common";
import {
    AbfLoanExpanded,
    BsMetaUtil,
    calculateLoanHealth,
    getZcLedger,
    getZcLoanCollateral,
    IncreaseCreditTransactionParams,
    serializeStrategyDuration,
    useActiveWallet,
    useIncreasePrincipalTransaction,
    useBestQuotes,
    getCollateralMintToInfo,
    useStrategyDurations,
    useMaxQuotesForCollateral,
    useOraclePrices,
    PriceFetchType,
    Ledger,
    BestQuote,
    LedgerExpanded,
    CollateralWithMaxQuote
} from "@bridgesplit/abf-react";
import { StatColumn, TokenInput, StatProps, TagTextAlert } from "@bridgesplit/ui";
import { useTransactionSender } from "app/components/transactions";
import {
    Result,
    LOADING_ERROR,
    MISSING_PARAM_ERROR,
    formatPercent,
    bsMath,
    uiAmountToLamports,
    formatDate,
    roundDownToDecimals,
    decimalsToBps
} from "@bridgesplit/utils";
import { getDurationIndex } from "@bridgesplit/abf-sdk";
import { COPY } from "app/constants";
import { TrackTransactionEvent } from "app/types";

import { ActionProps } from "./types";
import { calculateOptionsWithOffsets, RefinanceOption, RefinanceSelect } from "./RefinanceLoan";

/**
 * @dev Buffer to apply to LTV to ensure borrow is within safe limits
 */
const BORROW_MORE_LTV_BUFFER = 0.01;

export default function IncreasePrincipal({ loanExpanded }: ActionProps) {
    const [additionalPrincipal, setAdditionalPrincipal] = useState<number | undefined>(undefined);
    const [optionState, setOption] = useState<RefinanceOption>();

    const { strategyDurations } = useStrategyDurations();

    const firstLedger = getZcLedger(loanExpanded); //TODO: handle multiple ledgers in the action props
    const loanCollateral = getZcLoanCollateral(loanExpanded);

    const { getOracle } = useOraclePrices(
        [firstLedger?.principalMint, loanCollateral?.loanCollateral?.metadata.mint],
        PriceFetchType.Twap
    );

    const newPrincipalAmount = (firstLedger?.ledgerDebt.total ?? 0) + (additionalPrincipal ?? 0);

    // Define refinanceParams as a constant
    const refinanceParams = {
        refinanceStrategy: firstLedger?.strategy ?? "",
        principalRepayment: uiAmountToLamports(newPrincipalAmount, loanExpanded?.principalMetadata.decimals),
        minLqt: decimalsToBps(firstLedger?.lqtRatios[0] ?? 0),
        additionalBorrowAmount: uiAmountToLamports(additionalPrincipal, loanExpanded?.principalMetadata.decimals)
    };

    const shouldSkipBestQuotes = !newPrincipalAmount || !loanExpanded || !firstLedger;

    // Use the defined constant and improved skip logic
    const { data: quotes } = useBestQuotes({
        collateralMintToInfo: getCollateralMintToInfo(loanExpanded),
        principalMint: firstLedger?.principalMint,
        strategyDurations,
        showSelf: false,
        skip: shouldSkipBestQuotes,
        minPrincipalAmountLamports: uiAmountToLamports(newPrincipalAmount, loanExpanded?.principalMetadata.decimals),
        refinanceParams
    });

    const optionsWithOffsets = calculateOptionsWithOffsets({
        quotes,
        strategyDurations,
        newPrincipalAmount,
        loanExpanded
    });

    const optionsWithQuotes = optionsWithOffsets?.filter((o) => !!o.bestQuote);
    const validOptions = optionsWithQuotes?.filter((o) => !!o.bestQuote);
    const option = optionState ?? validOptions?.[0];

    const rawLoanCollateral = getZcLoanCollateral(loanExpanded);

    const parsedLoanCollateral = rawLoanCollateral
        ? {
              ...rawLoanCollateral,
              usdPrice: rawLoanCollateral.loanCollateral.usdPrice ?? 0,
              usdValue: rawLoanCollateral.loanCollateral.amount * (rawLoanCollateral.loanCollateral.usdPrice ?? 0),
              oracle: getOracle(rawLoanCollateral.loanCollateral.metadata.mint) ?? null,
              amount: rawLoanCollateral.loanCollateral.amount,
              metadata: {
                  ...rawLoanCollateral.loanCollateral.metadata,
                  whirlpoolExpanded: rawLoanCollateral.loanCollateral.metadata.whirlpoolExpanded
              }
          }
        : undefined;

    const { data: maxQuotes } = useMaxQuotesForCollateral({
        collateral: parsedLoanCollateral ? [parsedLoanCollateral] : [],
        principalMint: firstLedger?.principalMint,
        strategyDuration: option?.strategyDuration
    });

    // Check if this is a refinance (different strategy or duration)
    const isRefinance = useMemo(() => checkIsRefinanceNeeded(firstLedger, option), [firstLedger, option]);

    // Calculate max additional principal based on selected option
    const additionalPrincipalLimit = useMemo(() => {
        return calculateAdditionalBorrowLimits({
            loanExpanded,
            newLtv: option?.bestQuote?.ltv ?? 0,
            firstLedger,
            maxQuotes
        });
    }, [maxQuotes, loanExpanded, firstLedger, option]);

    const newApy = option?.bestQuote?.apy;
    const send = useTransactionSender();
    const { user } = useActiveWallet();
    const { activeWallet } = useActiveWallet();

    const increasePrincipal = useIncreasePrincipalTransaction();

    const submit = useCallback(async () => {
        const firstLedger = getZcLedger(loanExpanded);
        if (
            !user ||
            !loanExpanded ||
            !activeWallet ||
            !firstLedger ||
            !option?.bestQuote ||
            !option.bestQuote.lendingStrategyAddress
        )
            return Result.errFromMessage(LOADING_ERROR);

        const durationIndex = getDurationIndex({
            duration: option?.bestQuote?.duration,
            durationType: option?.bestQuote?.durationType
        });

        if (!additionalPrincipal || additionalPrincipalLimit === undefined || durationIndex === undefined)
            return Result.errFromMessage(MISSING_PARAM_ERROR);

        const params: IncreaseCreditTransactionParams = {
            decimals: loanExpanded.principalMetadata?.decimals ?? 0,
            loan: loanExpanded.loan.address,
            borrowParams: {
                amount: additionalPrincipal,
                expectedLoanValues: {
                    expectedApy: firstLedger.apy,
                    expectedLiquidationThreshold: firstLedger.lqtRatios
                }
            },
            strategy: firstLedger.strategy
        };

        // If this is a refinance, update params accordingly
        if (isRefinance) {
            params.borrowParams.expectedLoanValues.expectedApy = option.bestQuote.apy;
            params.borrowParams.expectedLoanValues.expectedLiquidationThreshold = [
                option.bestQuote.liquidationThreshold,
                0,
                0,
                0,
                0
            ]; //TODO: handle multiple ledgers
            params.refinanceParams = {
                newStrategy: option.bestQuote.lendingStrategyAddress,
                refinanceParams: {
                    ledgerIndex: 0,
                    durationIndex
                }
            };
        }

        return await send(increasePrincipal, params, {
            description: "Borrowing more",
            mixpanelEvent: { key: TrackTransactionEvent.IncreasePrincipal, params }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        activeWallet,
        user,
        increasePrincipal,
        loanExpanded,
        additionalPrincipal,
        additionalPrincipalLimit,
        send,
        option?.bestQuote,
        isRefinance
    ]);

    const maxBorrowAlert = useMemo(() => {
        if (!additionalPrincipalLimit || !additionalPrincipal) return undefined;

        if (additionalPrincipalLimit.additionalBorrowLimit < additionalPrincipal) {
            if (additionalPrincipalLimit.additionalBorrowLimitFromQuote < additionalPrincipal)
                return "There are not enough available funds to borrow this amount";
            if (additionalPrincipalLimit.maxAdditionalBorrowFromLtv < additionalPrincipal)
                return "You do not have enough collateral to borrow this amount";
        }
        return undefined;
    }, [additionalPrincipalLimit, additionalPrincipal]);

    return (
        <>
            <RefinanceSelect
                label="Rate fixed for"
                option={option}
                validOptions={validOptions}
                optionsWithOffsets={optionsWithOffsets}
                optionsWithQuotes={optionsWithQuotes}
                setOption={setOption}
            />
            <TokenInput
                maxLoading={!option?.bestQuote || !additionalPrincipalLimit}
                label="Amount"
                maxText="Max: "
                symbol={BsMetaUtil.getSymbol(loanExpanded?.principalMetadata)}
                decimals={loanExpanded?.principalMetadata.decimals}
                maxAmount={additionalPrincipalLimit?.additionalBorrowLimit}
                value={additionalPrincipal}
                setValue={setAdditionalPrincipal}
            />
            {maxBorrowAlert && (
                <TagTextAlert color="warning" icon="warning">
                    {maxBorrowAlert}
                </TagTextAlert>
            )}

            {!!additionalPrincipal && !!option?.bestQuote && (
                <Stats
                    loanExpanded={loanExpanded}
                    option={option}
                    additionalPrincipal={additionalPrincipal}
                    newApy={newApy}
                    isRefinance={isRefinance}
                />
            )}
            <AppButton
                isTransaction
                disabled={
                    !option?.bestQuote ||
                    !additionalPrincipal ||
                    !additionalPrincipalLimit?.additionalBorrowLimit ||
                    additionalPrincipal > additionalPrincipalLimit.additionalBorrowLimit
                }
                asyncCta={{ onClickWithResult: submit }}
                fullWidth
            >
                {"Borrow more"}
            </AppButton>
        </>
    );
}

type IncreasePrincipalStatsProps = {
    loanExpanded: AbfLoanExpanded | undefined;
    option: RefinanceOption | undefined;
    additionalPrincipal: number | undefined;
    newApy: number | undefined;
    isRefinance: boolean;
};

function Stats(props: IncreasePrincipalStatsProps) {
    const { loanExpanded, newApy, option, isRefinance } = props;
    const { health: originalHealth } = calculateLoanHealth(loanExpanded);
    const { ltv, liquidationThreshold } = calculateLoanHealth(loanExpanded);

    const firstLedger = getZcLedger(loanExpanded);

    // Calculate total principal (existing + additional)
    const totalPrincipal = (firstLedger?.ledgerDebt.total ?? 0) + (props.additionalPrincipal ?? 0);

    // If refinancing, use the option's total debt calculation which includes interest
    // Otherwise use the simpler calculation from getNewInterest
    const newTotalPrincipal = totalPrincipal;

    const newHealth = option?.healthRatio;
    const newLtv = option?.ltv;

    const stats: StatProps[] = [
        {
            caption: "APY",
            value: [formatPercent(loanExpanded?.borrowerWAvgApy), formatPercent(option?.bestQuote?.apy ?? newApy)],
            hide: loanExpanded?.borrowerWAvgApy === (option?.bestQuote?.apy ?? newApy)
        },
        {
            caption: "Health",
            value: <HealthChange currentHealth={originalHealth} previousHealth={newHealth} />,
            hide: !originalHealth,
            tooltip: COPY.HEALTH_FACTOR_TOOLTIP
        },
        {
            caption: "LTV",
            value: <LtvChange currentLtv={ltv} previousLtv={newLtv} liquidationThreshold={liquidationThreshold} />,
            hide: !ltv || !newLtv || !liquidationThreshold
        },
        {
            caption: "Borrowed",
            value: [firstLedger?.principalDue, newTotalPrincipal],
            hide: !firstLedger?.ledgerDebt,
            symbol: BsMetaUtil.getSymbol(loanExpanded?.principalMetadata)
        }
    ];

    // Add due date when refinancing
    if (isRefinance && option?.loanEndDate) {
        stats.push({
            caption: "Rate fixed until",
            value: formatDate(option.loanEndDate)
        });
    }

    return <StatColumn loading={!option} stats={stats} />;
}

function checkIsRefinanceNeeded(
    firstLedger: Ledger | undefined,
    option: { bestQuote: BestQuote | undefined } | undefined
): boolean {
    if (!firstLedger || !option?.bestQuote) return false;

    // Different strategy
    if (firstLedger.strategy !== option.bestQuote.lendingStrategyAddress) return true;

    // Different duration
    const currentDuration = {
        duration: firstLedger.duration,
        durationType: firstLedger.durationType
    };
    const newDuration = {
        duration: option.bestQuote.duration,
        durationType: option.bestQuote.durationType
    };
    return serializeStrategyDuration(currentDuration) !== serializeStrategyDuration(newDuration);
}

function calculateAdditionalBorrowLimits({
    loanExpanded,
    newLtv,
    firstLedger,
    maxQuotes
}: {
    loanExpanded: AbfLoanExpanded | undefined;
    newLtv: number;
    firstLedger: LedgerExpanded | undefined;
    maxQuotes: CollateralWithMaxQuote[] | undefined;
}):
    | {
          additionalBorrowLimit: number;
          additionalBorrowLimitFromQuote: number;
          maxAdditionalBorrowFromLtv: number;
      }
    | undefined {
    const loanCollateral = getZcLoanCollateral(loanExpanded);
    if (!maxQuotes || !loanExpanded || !firstLedger || !loanCollateral) return undefined;

    const maxBorrow = maxQuotes?.find((q) => q.metadata.mint === loanCollateral?.loanCollateral?.metadata.mint)
        ?.maxQuote?.maxBorrow;

    const additionalBorrowLimitFromQuote = Math.max(
        roundDownToDecimals(
            (bsMath.sub(maxBorrow, firstLedger?.ledgerDebt.total) ?? 0) * (1 - BORROW_MORE_LTV_BUFFER),
            loanExpanded?.principalMetadata.decimals ?? 0
        ),
        0
    );

    if (loanCollateral === undefined) return undefined;
    const loanCollateralValue = loanCollateral.loanCollateral.amount * (loanCollateral.loanCollateral.usdPrice ?? 0);
    const newLtvWithBuffer = bsMath.mul(newLtv, 1 - BORROW_MORE_LTV_BUFFER);
    const maxPrincipalUsd = bsMath.mul(loanCollateralValue, newLtvWithBuffer);
    const principalPrice = loanExpanded?.principalUsdPrice;
    if (principalPrice === undefined) return undefined;
    const maxPrincipalAmount = bsMath.div(maxPrincipalUsd, principalPrice);

    if (maxPrincipalAmount === undefined) return undefined;

    const maxAdditionalBorrowFromLtv = Math.max(
        roundDownToDecimals(
            bsMath.sub(maxPrincipalAmount, firstLedger.ledgerDebt.total) ?? 0,
            loanExpanded?.principalMetadata.decimals ?? 0
        ),
        0
    );
    const additionalBorrowLimit = Math.min(additionalBorrowLimitFromQuote, maxAdditionalBorrowFromLtv);

    return { additionalBorrowLimit, additionalBorrowLimitFromQuote, maxAdditionalBorrowFromLtv };
}
